const DEFAULT_STATE = { items: {}, status: {} };

export default function (state = DEFAULT_STATE) {
  return state;
}

function updateApiRequests(state, action, updatesArg) {
  const { cacheGroupKey, meta, parentRequestKey, requestKey } = action;
  const rootKey = parentRequestKey || requestKey;
  const updates = { ...updatesArg, ...meta };
  return {
    ...state,
    [rootKey]: {
      ...state[rootKey],
      ...(parentRequestKey &&
        requestKey != null && {
          requests: {
            ...state[rootKey]?.requests,
            [requestKey]: { ...state[rootKey]?.requests?.[requestKey], ...updates },
          },
        }),
      ...updates,
    },
    ...(cacheGroupKey && {
      __cacheGroups: {
        ...state.__cacheGroups,
        [cacheGroupKey]: [...(state.__cacheGroups?.[cacheGroupKey] || []), rootKey],
      },
    }),
  };
}

export const combinedReducerEnhancer = reducer => (state, action) => {
  if (
    action.error?.status === 401 &&
    (action.error.body?.error_code === 'reauthentication_required' ||
      action.error.body?.error_code === 'unauthenticated')
  ) {
    state = { ...state, appState: { ...state.appState, show_reauthentication_form: true } };
  }

  switch (action.type) {
    case 'STANDARD_API_REQUEST':
      return {
        ...state,
        apiRequests: updateApiRequests(state.apiRequests, action, {
          fetching: true,
          // Stashed beside the request rather than handed back through the action, so that the
          // failure below can find it without every caller having to carry it around.
          __instantRollback: action.instantUpdate && instantRollbackFor(action.instantUpdate, state),
        }),
        ...addPayloadToState(action.instantUpdate, state),
      };
    case 'STANDARD_API_REQUEST_SUCCESS': {
      const newState = {
        ...state,
        apiRequests: updateApiRequests(state.apiRequests, action, {
          fetching: false,
          success: true,
          error: undefined,
          fetched_at: Date.now(),
          __instantRollback: undefined,
        }),
        ...addPayloadToState(action.payload, state),
      };
      if (action.meta?._destroy) {
        Object.entries(action.meta._destroy).forEach(([entityType, entityIds]) => {
          if (!entityIds?.length) return;
          const newItems = { ...newState[entityType].items };
          entityIds.forEach(entityId => delete newItems[entityId]);
          newState[entityType] = { ...newState[entityType], items: newItems };
        });
      }
      return newState;
    }
    case 'STANDARD_API_REQUEST_FAIL': {
      // Reverted before the server's own correction is applied, so a failure that comes with a
      // `payload_update` still has the last word on the keys it mentions.
      const rollback = state.apiRequests[action.parentRequestKey || action.requestKey]?.__instantRollback;
      const reverted = rollback ? { ...state, ...revertInstantUpdate(rollback, state) } : state;
      return {
        ...reverted,
        apiRequests: updateApiRequests(reverted.apiRequests, action, {
          fetching: false,
          error: action.error,
          fetched_at: Date.now(),
          __instantRollback: undefined,
        }),
        ...addPayloadToState(action.payload, reverted),
      };
    }
    case 'STANDARD_API_DELETE_REQUEST': {
      if (state.apiRequests[action.key]) {
        const newApiRequests = { ...state.apiRequests };
        delete newApiRequests[action.key];
        return { ...state, apiRequests: newApiRequests };
      }
      return state;
    }
    case 'STANDARD_API_DELETE_CACHE_GROUP_REQUESTS': {
      const requestKeys = state.apiRequests.__cacheGroups?.[action.key];
      if (requestKeys) {
        const newApiRequests = { ...state.apiRequests };
        const newCacheGroups = { ...newApiRequests.__cacheGroups };
        delete newCacheGroups[action.key];
        requestKeys.forEach(k => delete newApiRequests[k]);
        newApiRequests.__cacheGroups = newCacheGroups;
        return { ...state, apiRequests: newApiRequests };
      }
      return state;
    }
    case 'STANDARD_PUSH_UPDATE': {
      return {
        ...state,
        ...addPayloadToState(action.payload, state),
      };
    }
    default:
      return reducer(state, action);
  }
};

/**
 * What an `instantUpdate` is about to overwrite, so that a request which fails can put it back.
 * An optimistic write is applied on dispatch and has no reply coming to correct it, so without
 * this a failed one leaves the screen showing something that never happened.
 *
 * Both halves are kept: `previous` to restore, and `applied` so the restore can tell whether
 * anything has written the key since. A record the store has never seen is skipped — there is
 * no earlier version of it to go back to.
 */
function instantRollbackFor(instantUpdate, state) {
  return Object.entries(instantUpdate).reduce((mem, [entityType, entities]) => {
    const items = state[entityType]?.items;
    if (!items) return mem;

    const rows = entities
      .filter(entity => items[entity.id])
      .map(({ id, ...applied }) => ({
        applied,
        id,
        previous: Object.fromEntries(Object.keys(applied).map(key => [key, items[id][key]])),
      }));
    if (rows.length) mem[entityType] = rows;
    return mem;
  }, {});
}

// Undoes an optimistic write key by key, and only where the value is still the one it wrote:
// anything else means a later write owns that key now — another optimistic write, or a reply
// that landed in between — and putting the old copy back would undo it. Identity is the right
// test because the store holds the very value the write handed it.
function revertInstantUpdate(rollback, state) {
  return Object.entries(rollback).reduce((mem, [entityType, entities]) => {
    const items = { ...state[entityType].items };
    let reverted = false;
    entities.forEach(({ applied, id, previous }) => {
      const item = items[id];
      if (!item) return;
      const stale = Object.keys(applied).filter(key => item[key] === applied[key]);
      if (!stale.length) return;
      reverted = true;
      items[id] = { ...item, ...Object.fromEntries(stale.map(key => [key, previous[key]])) };
    });
    // Left alone when there was nothing to put back, so subscribers to an untouched collection
    // are not re-rendered by a failure that changed nothing for them.
    if (reverted) mem[entityType] = { ...state[entityType], items };
    return mem;
  }, {});
}

function addPayloadToState(payload, state) {
  if (!payload) return;
  return Object.entries(payload).reduce((mem, [entityType, entities]) => {
    mem[entityType] = {
      ...state[entityType],
      items: entities.reduce(
        (mem2, entity) => {
          mem2[entity.id] = {
            ...mem2[entity.id],
            ...entity,
          };
          return mem2;
        },
        {
          ...state[entityType].items,
        },
      ),
    };
    return mem;
  }, {});
}
